import * as THREE from "three";
import { getSymbol3DPlanScale } from "../../../utils/symbolFlipState";
import {
  SymbolType,
  WallShapeType,
  WallType,
  isCircleStairsType,
  isRectStairsType,
  isTurnStairsType,
  isSvgType,
} from "../../../types/floorplannerTypes";
import { getQuadraticBezierPoint } from "../../../utils/arcUtils";
import {
  getPrimitiveSymbol3DDefaults,
  getPrimitiveSymbol3DModelKeyForSymbol,
} from "../symbols/registry/primitiveSymbol3DConfig";
import { getSymbol3DDefinition } from "../symbols/registry/symbol3DRegistry";
import {
  createProjectedShapePrimitiveFootprintPolygon,
  getShapePrimitiveDimensions,
  isShapePrimitiveSymbol,
} from "../symbols/primitives/shapes/shapePrimitiveGeometry";
import { getTurnStairsPlanBounds } from "../symbols/primitives/stairs/stairGeometry";

const MIN_FOOTPRINT_SIZE = 0.04;
const ARC_WALL_COLLISION_SEGMENTS = 24;
const EPSILON = 1e-8;
// How precisely (in world units) the swept contact search homes in on the
// boundary, plus a hard cap on bisection steps so a pathologically large pointer
// jump can't blow up the per-frame cost.
const CONTACT_TOLERANCE = 1e-3;
const MAX_CONTACT_ITERATIONS = 24;
const DEFAULT_WALL_SNAP_TOLERANCE = 0.5;
const CIRCLE_STAIR_FOOTPRINT_SEGMENT_ANGLE = Math.PI / 18;
const MIN_STAIR_OPEN_ANGLE = THREE.MathUtils.degToRad(2);

export type Symbol3DDragCollisionInput = {
  symbol: SymbolType;
  candidatePosition: THREE.Vector2;
  walls: readonly WallType[];
  wallShapesById?: Map<string, WallShapeType[]>;
  defaultWallWidth: number;
  wallSnapTolerance?: number;
  wallSnapReferencePosition?: THREE.Vector2;
};

export type Symbol3DDragResolutionInput = Symbol3DDragCollisionInput & {
  lastValidPosition: THREE.Vector2;
  bypassCollision?: boolean;
};

export function isDraggableLoose3DSymbol(symbol: SymbolType): boolean {
  const definition = getSymbol3DDefinition(symbol);
  if (isShapePrimitiveSymbol(symbol)) {
    return symbol.threeD?.collision !== "none";
  }

  return (
    definition.renderMode === "primitive" &&
    (definition.kind === "primitiveSymbol" ||
      definition.kind === "circleStairs" ||
      definition.kind === "rectStairs" ||
      definition.kind === "turnStairs") &&
    symbol.threeD?.collision !== "none"
  );
}

export function canPlaceSymbol3DAtPosition(
  input: Symbol3DDragCollisionInput,
): boolean {
  const symbolFootprint = createSymbol3DFootprintPolygon(
    input.symbol,
    input.candidatePosition,
    input.walls,
  );
  if (!symbolFootprint) return true;

  return !input.walls.some((wall) => {
    if (
      input.symbol.threeD?.placementMode === "wall" &&
      input.symbol.threeD.wallMount?.wallId === wall.id
    ) {
      return false;
    }
    return wallCollisionBlocksSymbolFootprint(
      wall,
      symbolFootprint,
      input.defaultWallWidth,
      {
        wallShapes: input.wallShapesById?.get(wall.id),
        wallSnapTolerance: input.wallSnapTolerance,
      },
    );
  });
}

/**
 * Walks from a known-valid position toward a blocked one using bisection and
 * returns the furthest position along that segment that is still placeable.
 *
 * This turns discrete drag updates into a swept (continuous) collision test: the
 * stop position is decided by geometry, not by how far the pointer happened to
 * jump between frames. Without it, a fast drag (or a slow machine firing fewer
 * pointer events) settles wherever the previous frame landed, which can be well
 * short of the real boundary.
 */
export function findFurthestPlaceablePosition(
  validPosition: THREE.Vector2,
  blockedPosition: THREE.Vector2,
  canPlace: (position: THREE.Vector2) => boolean,
  tolerance = CONTACT_TOLERANCE,
  maxIterations = MAX_CONTACT_ITERATIONS,
): THREE.Vector2 {
  let nearestValid = validPosition.clone();
  let blocked = blockedPosition.clone();
  const toleranceSq = tolerance * tolerance;

  for (let index = 0; index < maxIterations; index += 1) {
    if (nearestValid.distanceToSquared(blocked) <= toleranceSq) break;
    const mid = nearestValid.clone().add(blocked).multiplyScalar(0.5);
    if (canPlace(mid)) {
      nearestValid = mid;
    } else {
      blocked = mid;
    }
  }

  return nearestValid;
}

export function resolveSymbol3DDragPosition(
  input: Symbol3DDragResolutionInput,
): THREE.Vector2 {
  if (input.bypassCollision) {
    return input.candidatePosition.clone();
  }

  const wallSnapReferencePosition =
    input.wallSnapReferencePosition ?? input.lastValidPosition;
  const dragInput = { ...input, wallSnapReferencePosition };

  if (canPlaceSymbol3DAtPosition(dragInput)) {
    return input.candidatePosition.clone();
  }

  const canPlaceAt = (candidatePosition: THREE.Vector2) =>
    canPlaceSymbol3DAtPosition({ ...dragInput, candidatePosition });

  const symbolFootprint = createSymbol3DFootprintPolygon(
    input.symbol,
    input.candidatePosition,
    input.walls,
  );
  if (symbolFootprint) {
    const oppositeSnapPosition = resolveOppositeWallSnapPosition({
      walls: input.walls,
      symbolFootprint,
      candidatePosition: input.candidatePosition,
      defaultWallWidth: input.defaultWallWidth,
      wallShapesById: input.wallShapesById,
      wallSnapTolerance: input.wallSnapTolerance,
      wallSnapReferencePosition,
      canPlaceAt,
    });
    if (oppositeSnapPosition) {
      return oppositeSnapPosition;
    }
  }

  if (!canPlaceAt(input.lastValidPosition)) {
    return input.candidatePosition.clone();
  }

  // The pointer jumped from a valid position straight into a wall. Find the
  // actual contact point along the movement segment so we hug the boundary
  // regardless of how large the jump was.
  const contactPosition = findFurthestPlaceablePosition(
    input.lastValidPosition,
    input.candidatePosition,
    canPlaceAt,
  );

  const collidingWall = findClosestCollidingWall(dragInput);
  if (collidingWall) {
    const tangent = getWallTangentNearPoint(collidingWall, contactPosition);
    // Project the remaining (blocked) movement onto the wall and slide along it
    // starting from the contact point, so the symbol keeps moving parallel to
    // the wall while staying pressed against it.
    const delta = input.candidatePosition.clone().sub(contactPosition);
    const slideDistance = delta.dot(tangent);
    if (Math.abs(slideDistance) > EPSILON) {
      const slideTarget = contactPosition
        .clone()
        .add(tangent.multiplyScalar(slideDistance));
      if (canPlaceAt(slideTarget)) {
        return slideTarget;
      }
      // Sliding ran into another wall (e.g. a corner); advance as far as
      // possible along the slide direction instead of giving up.
      const slideContact = findFurthestPlaceablePosition(
        contactPosition,
        slideTarget,
        canPlaceAt,
      );
      if (slideContact.distanceToSquared(contactPosition) > EPSILON) {
        return slideContact;
      }
    }
  }

  const axisCandidates = [
    new THREE.Vector2(input.candidatePosition.x, contactPosition.y),
    new THREE.Vector2(contactPosition.x, input.candidatePosition.y),
  ].sort(
    (a, b) =>
      b.distanceToSquared(contactPosition) -
      a.distanceToSquared(contactPosition),
  );

  const validAxisCandidate = axisCandidates.find((candidatePosition) =>
    canPlaceAt(candidatePosition),
  );

  return validAxisCandidate?.clone() ?? contactPosition.clone();
}

export function createSymbol3DFootprintPolygon(
  symbol: SymbolType,
  position = symbol.position,
  walls: readonly WallType[] = [],
): THREE.Vector2[] | null {
  if (isShapePrimitiveSymbol(symbol)) {
    const candidateSymbol =
      position === symbol.position
        ? symbol
        : {
            ...symbol,
            position,
          };
    return createProjectedShapePrimitiveFootprintPolygon(candidateSymbol, walls);
  }

  if (isRectStairsType(symbol)) {
    return createRectStairFootprintPolygon(symbol, position);
  }

  if (isCircleStairsType(symbol)) {
    return createCircleStairFootprintPolygon(symbol, position);
  }

  if (isTurnStairsType(symbol)) {
    return createTurnStairFootprintPolygon(symbol, position);
  }

  const footprint = getSymbol3DFootprint(symbol);
  if (!footprint) return null;

  const halfWidth = footprint.width / 2;
  const halfDepth = footprint.depth / 2;
  const corners = [
    new THREE.Vector2(-halfWidth, -halfDepth),
    new THREE.Vector2(halfWidth, -halfDepth),
    new THREE.Vector2(halfWidth, halfDepth),
    new THREE.Vector2(-halfWidth, halfDepth),
  ];

  return transformLocalFootprint(corners, symbol, position);
}

function createRectStairFootprintPolygon(
  symbol: SymbolType,
  position: THREE.Vector2,
): THREE.Vector2[] {
  if (!isRectStairsType(symbol)) return [];

  const halfWidth = sanitizeFootprintDimension(symbol.rectStairsWidth) / 2;
  const halfDepth = sanitizeFootprintDimension(symbol.rectStairsHeight) / 2;

  return transformLocalFootprint(
    [
      new THREE.Vector2(-halfWidth, -halfDepth),
      new THREE.Vector2(halfWidth, -halfDepth),
      new THREE.Vector2(halfWidth, halfDepth),
      new THREE.Vector2(-halfWidth, halfDepth),
    ],
    symbol,
    position,
  );
}

function createTurnStairFootprintPolygon(
  symbol: SymbolType,
  position: THREE.Vector2,
): THREE.Vector2[] {
  if (!isTurnStairsType(symbol)) return [];

  const bounds = getTurnStairsPlanBounds(symbol, {
    defaultStepSize: symbol.stairStepSize ?? 0.32,
  });
  const halfWidth = sanitizeFootprintDimension(bounds.width) / 2;
  const halfDepth = sanitizeFootprintDimension(bounds.depth) / 2;

  return transformLocalFootprint(
    [
      new THREE.Vector2(-halfWidth, -halfDepth),
      new THREE.Vector2(halfWidth, -halfDepth),
      new THREE.Vector2(halfWidth, halfDepth),
      new THREE.Vector2(-halfWidth, halfDepth),
    ],
    symbol,
    position,
  );
}

function createCircleStairFootprintPolygon(
  symbol: SymbolType,
  position: THREE.Vector2,
): THREE.Vector2[] {
  if (!isCircleStairsType(symbol)) return [];

  const radius = sanitizeFootprintDimension(symbol.circleStairsWidth);
  const openAngle = sanitizeCircleStairOpenAngle(symbol.openAngle);
  const segmentCount = Math.max(
    4,
    Math.ceil(openAngle / CIRCLE_STAIR_FOOTPRINT_SEGMENT_ANGLE),
  );
  const points = [new THREE.Vector2(0, 0)];

  for (let index = 0; index <= segmentCount; index += 1) {
    const angle = (index / segmentCount) * openAngle;
    points.push(
      new THREE.Vector2(radius * Math.cos(angle), radius * Math.sin(angle)),
    );
  }

  return transformLocalFootprint(points, symbol, position);
}

function transformLocalFootprint(
  points: THREE.Vector2[],
  symbol: SymbolType,
  position: THREE.Vector2,
): THREE.Vector2[] {
  const rotation = symbol.rotation ?? 0;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const [flipX, flipY] = getSymbol3DPlanScale(symbol);

  return points.map((point) => {
    const localX = point.x * flipX;
    const localY = point.y * flipY;
    return new THREE.Vector2(
      position.x + localX * cos - localY * sin,
      position.y + localX * sin + localY * cos,
    );
  });
}

function sanitizeFootprintDimension(value: number | undefined): number {
  return Number.isFinite(value) && typeof value === "number"
    ? Math.max(Math.abs(value), MIN_FOOTPRINT_SIZE)
    : MIN_FOOTPRINT_SIZE;
}

function sanitizeCircleStairOpenAngle(value: number | undefined): number {
  return THREE.MathUtils.clamp(
    Number.isFinite(value) && typeof value === "number"
      ? Math.abs(value)
      : Math.PI,
    MIN_STAIR_OPEN_ANGLE,
    Math.PI * 2,
  );
}

export function createWallCollisionPolygons(
  wall: WallType,
  defaultWallWidth: number,
  wallShapes?: WallShapeType[],
): THREE.Vector2[][] {
  const shapePolygons =
    wallShapes
      ?.map((shape) => sanitizePolygon(shape.paths))
      .filter((polygon): polygon is THREE.Vector2[] => polygon.length >= 3) ??
    [];

  if (shapePolygons.length > 0) return shapePolygons;

  const wallWidth = Math.max(wall.wallWidth ?? defaultWallWidth, EPSILON);
  if (wall.lineForm === "arc" && wall.controlPoint) {
    return [createArcWallCollisionPolygon(wall, wallWidth)].filter(
      (polygon) => polygon.length >= 3,
    );
  }

  return [createStraightWallCollisionPolygon(wall, wallWidth)].filter(
    (polygon) => polygon.length >= 3,
  );
}

export function polygonsOverlap(
  polygonA: THREE.Vector2[],
  polygonB: THREE.Vector2[],
): boolean {
  const a = sanitizePolygon(polygonA);
  const b = sanitizePolygon(polygonB);
  if (a.length < 3 || b.length < 3) return false;

  for (let aIndex = 0; aIndex < a.length; aIndex += 1) {
    const aStart = a[aIndex];
    const aEnd = a[(aIndex + 1) % a.length];
    for (let bIndex = 0; bIndex < b.length; bIndex += 1) {
      const bStart = b[bIndex];
      const bEnd = b[(bIndex + 1) % b.length];
      if (segmentsIntersect(aStart, aEnd, bStart, bEnd)) return true;
    }
  }

  return isPointInPolygon(a[0], b) || isPointInPolygon(b[0], a);
}

type WallCollisionOptions = {
  wallShapes?: WallShapeType[];
  wallSnapTolerance?: number;
};

type WallSnapSide = "positive" | "negative";

type WallProjection = {
  point: THREE.Vector2;
  tangent: THREE.Vector2;
};

type WallSnapBlock = {
  side: WallSnapSide;
  normal: THREE.Vector2;
  minSignedDistance: number;
  maxSignedDistance: number;
  halfWallWidth: number;
};

export function wallCollisionBlocksSymbolFootprint(
  wall: WallType,
  symbolFootprint: THREE.Vector2[],
  defaultWallWidth: number,
  options: WallCollisionOptions = {},
): boolean {
  return (
    getWallSnapBlockForFootprint(
      wall,
      symbolFootprint,
      defaultWallWidth,
      options,
    ) !== null
  );
}

export function resolveOppositeWallSnapPosition(input: {
  walls: readonly WallType[];
  symbolFootprint: THREE.Vector2[];
  candidatePosition: THREE.Vector2;
  defaultWallWidth: number;
  wallShapesById?: Map<string, WallShapeType[]>;
  wallSnapTolerance?: number;
  wallSnapReferencePosition?: THREE.Vector2;
  canPlaceAt: (position: THREE.Vector2) => boolean;
}): THREE.Vector2 | null {
  if (!input.wallSnapReferencePosition) return null;

  let bestPosition: THREE.Vector2 | null = null;
  let bestDistanceSq = Number.POSITIVE_INFINITY;

  input.walls.forEach((wall) => {
    const block = getWallSnapBlockForFootprint(
      wall,
      input.symbolFootprint,
      input.defaultWallWidth,
      {
        wallShapes: input.wallShapesById?.get(wall.id),
        wallSnapTolerance: input.wallSnapTolerance,
      },
    );
    if (!block) return;

    const referenceSide = getWallSideForPosition(
      input.wallSnapReferencePosition!,
      wall,
    );
    if (!referenceSide || referenceSide === block.side) return;

    const snapDelta = getWallSnapDelta(block);
    const snappedPosition = input.candidatePosition.clone().add(snapDelta);
    if (!input.canPlaceAt(snappedPosition)) return;

    const distanceSq = snapDelta.lengthSq();
    if (distanceSq < bestDistanceSq) {
      bestDistanceSq = distanceSq;
      bestPosition = snappedPosition;
    }
  });

  return bestPosition;
}

function getWallSnapBlockForFootprint(
  wall: WallType,
  symbolFootprint: THREE.Vector2[],
  defaultWallWidth: number,
  options: WallCollisionOptions = {},
): WallSnapBlock | null {
  const wallPolygons = createWallCollisionPolygons(
    wall,
    defaultWallWidth,
    options.wallShapes,
  );
  if (
    !wallPolygons.some((wallPolygon) =>
      polygonsOverlap(symbolFootprint, wallPolygon),
    )
  ) {
    return null;
  }

  const projection = projectPointToWallCenterline(
    getPolygonCenter(symbolFootprint),
    wall,
  );
  if (!projection) return null;

  const wallWidth = Math.max(wall.wallWidth ?? defaultWallWidth, EPSILON);
  const halfWallWidth = wallWidth / 2;
  const snapTolerance = sanitizeWallSnapTolerance(options.wallSnapTolerance);
  const normal = new THREE.Vector2(-projection.tangent.y, projection.tangent.x);
  const signedDistances = symbolFootprint.map((point) =>
    point.clone().sub(projection.point).dot(normal),
  );
  const minSignedDistance = Math.min(...signedDistances);
  const maxSignedDistance = Math.max(...signedDistances);

  return getWallSnapBlock(
    minSignedDistance,
    maxSignedDistance,
    halfWallWidth,
    snapTolerance,
    normal,
  );
}

function findClosestCollidingWall(
  input: Symbol3DDragCollisionInput,
): WallType | null {
  const symbolFootprint = createSymbol3DFootprintPolygon(
    input.symbol,
    input.candidatePosition,
    input.walls,
  );
  if (!symbolFootprint) return null;

  let closestWall: WallType | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;

  input.walls.forEach((wall) => {
    if (
      !wallCollisionBlocksSymbolFootprint(
        wall,
        symbolFootprint,
        input.defaultWallWidth,
        {
          wallShapes: input.wallShapesById?.get(wall.id),
          wallSnapTolerance: input.wallSnapTolerance,
        },
      )
    ) {
      return;
    }

    const distance = distanceToWallCenterline(input.candidatePosition, wall);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestWall = wall;
    }
  });

  return closestWall;
}

function getWallTangentNearPoint(
  wall: WallType,
  point: THREE.Vector2,
): THREE.Vector2 {
  if (wall.lineForm === "arc" && wall.controlPoint) {
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    for (let index = 0; index <= ARC_WALL_COLLISION_SEGMENTS; index += 1) {
      const t = index / ARC_WALL_COLLISION_SEGMENTS;
      const sample = getQuadraticBezierPoint(
        t,
        wall.start,
        wall.controlPoint,
        wall.end,
      );
      const distance = sample.distanceToSquared(point);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    }

    const previousT =
      Math.max(closestIndex - 1, 0) / ARC_WALL_COLLISION_SEGMENTS;
    const nextT =
      Math.min(closestIndex + 1, ARC_WALL_COLLISION_SEGMENTS) /
      ARC_WALL_COLLISION_SEGMENTS;
    const previous = getQuadraticBezierPoint(
      previousT,
      wall.start,
      wall.controlPoint,
      wall.end,
    );
    const next = getQuadraticBezierPoint(
      nextT,
      wall.start,
      wall.controlPoint,
      wall.end,
    );
    const tangent = next.sub(previous);
    if (tangent.lengthSq() > EPSILON) return tangent.normalize();
  }

  const tangent = wall.end.clone().sub(wall.start);
  if (tangent.lengthSq() <= EPSILON) return new THREE.Vector2(1, 0);
  return tangent.normalize();
}

function distanceToWallCenterline(
  point: THREE.Vector2,
  wall: WallType,
): number {
  if (wall.lineForm === "arc" && wall.controlPoint) {
    let closestDistance = Number.POSITIVE_INFINITY;
    let previous = wall.start.clone();
    for (let index = 1; index <= ARC_WALL_COLLISION_SEGMENTS; index += 1) {
      const t = index / ARC_WALL_COLLISION_SEGMENTS;
      const next = getQuadraticBezierPoint(
        t,
        wall.start,
        wall.controlPoint,
        wall.end,
      );
      closestDistance = Math.min(
        closestDistance,
        distanceToSegment(point, previous, next),
      );
      previous = next;
    }
    return closestDistance;
  }

  return distanceToSegment(point, wall.start, wall.end);
}

function distanceToSegment(
  point: THREE.Vector2,
  start: THREE.Vector2,
  end: THREE.Vector2,
): number {
  const segment = end.clone().sub(start);
  const lengthSq = segment.lengthSq();
  if (lengthSq <= EPSILON) return point.distanceTo(start);

  const t = THREE.MathUtils.clamp(
    point.clone().sub(start).dot(segment) / lengthSq,
    0,
    1,
  );
  return point.distanceTo(start.clone().add(segment.multiplyScalar(t)));
}

function projectPointToWallCenterline(
  point: THREE.Vector2,
  wall: WallType,
): WallProjection | null {
  if (wall.lineForm === "arc" && wall.controlPoint) {
    let closestProjection: WallProjection | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    let previous = wall.start.clone();

    for (let index = 1; index <= ARC_WALL_COLLISION_SEGMENTS; index += 1) {
      const t = index / ARC_WALL_COLLISION_SEGMENTS;
      const next = getQuadraticBezierPoint(
        t,
        wall.start,
        wall.controlPoint,
        wall.end,
      );
      const projected = closestPointOnSegment(point, previous, next);
      const distance = projected.distanceToSquared(point);
      if (distance < closestDistance) {
        const tangent = next.clone().sub(previous);
        if (tangent.lengthSq() > EPSILON) {
          closestDistance = distance;
          closestProjection = {
            point: projected,
            tangent: tangent.normalize(),
          };
        }
      }
      previous = next;
    }

    return closestProjection;
  }

  const tangent = wall.end.clone().sub(wall.start);
  if (tangent.lengthSq() <= EPSILON) return null;

  return {
    point: closestPointOnSegment(point, wall.start, wall.end),
    tangent: tangent.normalize(),
  };
}

function closestPointOnSegment(
  point: THREE.Vector2,
  start: THREE.Vector2,
  end: THREE.Vector2,
): THREE.Vector2 {
  const segment = end.clone().sub(start);
  const lengthSq = segment.lengthSq();
  if (lengthSq <= EPSILON) return start.clone();

  const t = THREE.MathUtils.clamp(
    point.clone().sub(start).dot(segment) / lengthSq,
    0,
    1,
  );
  return start.clone().add(segment.multiplyScalar(t));
}

function getPolygonCenter(points: THREE.Vector2[]): THREE.Vector2 {
  if (points.length === 0) return new THREE.Vector2();

  return points
    .reduce((center, point) => center.add(point), new THREE.Vector2())
    .multiplyScalar(1 / points.length);
}

function getWallSideForPosition(
  position: THREE.Vector2,
  wall: WallType,
): WallSnapSide | null {
  const projection = projectPointToWallCenterline(position, wall);
  if (!projection) return null;

  const normal = new THREE.Vector2(-projection.tangent.y, projection.tangent.x);
  return position.clone().sub(projection.point).dot(normal) >= 0
    ? "positive"
    : "negative";
}

function getWallSnapBlock(
  minSignedDistance: number,
  maxSignedDistance: number,
  halfWallWidth: number,
  snapTolerance: number,
  normal: THREE.Vector2,
): WallSnapBlock | null {
  const positiveSidePenetration = halfWallWidth - minSignedDistance;
  const blocksPositiveSide =
    maxSignedDistance >= halfWallWidth - EPSILON &&
    positiveSidePenetration >= -EPSILON &&
    positiveSidePenetration <= snapTolerance + EPSILON;

  const negativeSidePenetration = maxSignedDistance + halfWallWidth;
  const blocksNegativeSide =
    minSignedDistance <= -halfWallWidth + EPSILON &&
    negativeSidePenetration >= -EPSILON &&
    negativeSidePenetration <= snapTolerance + EPSILON;

  if (blocksPositiveSide && blocksNegativeSide) {
    return positiveSidePenetration <= negativeSidePenetration
      ? {
          side: "positive",
          normal,
          minSignedDistance,
          maxSignedDistance,
          halfWallWidth,
        }
      : {
          side: "negative",
          normal,
          minSignedDistance,
          maxSignedDistance,
          halfWallWidth,
        };
  }

  if (blocksPositiveSide) {
    return {
      side: "positive",
      normal,
      minSignedDistance,
      maxSignedDistance,
      halfWallWidth,
    };
  }

  if (blocksNegativeSide) {
    return {
      side: "negative",
      normal,
      minSignedDistance,
      maxSignedDistance,
      halfWallWidth,
    };
  }

  return null;
}

function getWallSnapDelta(block: WallSnapBlock): THREE.Vector2 {
  const delta =
    block.side === "positive"
      ? block.halfWallWidth + CONTACT_TOLERANCE - block.minSignedDistance
      : -block.halfWallWidth - CONTACT_TOLERANCE - block.maxSignedDistance;

  return block.normal.clone().multiplyScalar(delta);
}

function sanitizeWallSnapTolerance(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return DEFAULT_WALL_SNAP_TOLERANCE;
  }

  return Math.max(0, value);
}

function getSymbol3DFootprint(
  symbol: SymbolType,
): { width: number; depth: number } | null {
  if (isShapePrimitiveSymbol(symbol)) {
    return getShapePrimitiveDimensions(symbol);
  }

  const modelKey = getPrimitiveSymbol3DModelKeyForSymbol(symbol);
  const defaults = getPrimitiveSymbol3DDefaults(modelKey);
  const productDimensions = symbol.threeD?.product?.dimensions;
  const width = isSvgType(symbol)
    ? symbol.svgLength
    : (productDimensions?.width ??
      symbol.threeD?.dimensions?.width ??
      defaults?.dimensions.width);
  const depth = isSvgType(symbol)
    ? symbol.svgHeight
    : (productDimensions?.depth ??
      symbol.threeD?.dimensions?.depth ??
      defaults?.dimensions.depth);

  if (!isPositiveFiniteNumber(width) || !isPositiveFiniteNumber(depth)) {
    return null;
  }

  return {
    width: Math.max(width, MIN_FOOTPRINT_SIZE),
    depth: Math.max(depth, MIN_FOOTPRINT_SIZE),
  };
}

function createStraightWallCollisionPolygon(
  wall: WallType,
  wallWidth: number,
): THREE.Vector2[] {
  const direction = wall.end.clone().sub(wall.start);
  if (direction.lengthSq() <= EPSILON) return [];

  direction.normalize();
  const normal = new THREE.Vector2(-direction.y, direction.x).multiplyScalar(
    wallWidth / 2,
  );

  return [
    wall.start.clone().add(normal),
    wall.end.clone().add(normal),
    wall.end.clone().sub(normal),
    wall.start.clone().sub(normal),
  ];
}

function createArcWallCollisionPolygon(
  wall: WallType,
  wallWidth: number,
): THREE.Vector2[] {
  if (!wall.controlPoint) return [];

  const centerline: THREE.Vector2[] = [];
  for (let index = 0; index <= ARC_WALL_COLLISION_SEGMENTS; index += 1) {
    const t = index / ARC_WALL_COLLISION_SEGMENTS;
    centerline.push(
      getQuadraticBezierPoint(t, wall.start, wall.controlPoint, wall.end),
    );
  }

  const left: THREE.Vector2[] = [];
  const right: THREE.Vector2[] = [];
  for (let index = 0; index < centerline.length; index += 1) {
    const previous = centerline[Math.max(index - 1, 0)];
    const next = centerline[Math.min(index + 1, centerline.length - 1)];
    const tangent = next.clone().sub(previous);
    if (tangent.lengthSq() <= EPSILON) continue;

    tangent.normalize();
    const normal = new THREE.Vector2(-tangent.y, tangent.x).multiplyScalar(
      wallWidth / 2,
    );
    left.push(centerline[index].clone().add(normal));
    right.push(centerline[index].clone().sub(normal));
  }

  return left.concat(right.reverse());
}

function sanitizePolygon(points: THREE.Vector2[]): THREE.Vector2[] {
  const sanitized: THREE.Vector2[] = [];
  points.forEach((point) => {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
    const previous = sanitized[sanitized.length - 1];
    if (!previous || previous.distanceToSquared(point) > EPSILON * EPSILON) {
      sanitized.push(point.clone());
    }
  });

  if (
    sanitized.length > 1 &&
    sanitized[0].distanceToSquared(sanitized[sanitized.length - 1]) <=
      EPSILON * EPSILON
  ) {
    sanitized.pop();
  }

  return sanitized;
}

function segmentsIntersect(
  aStart: THREE.Vector2,
  aEnd: THREE.Vector2,
  bStart: THREE.Vector2,
  bEnd: THREE.Vector2,
): boolean {
  const o1 = orientation(aStart, aEnd, bStart);
  const o2 = orientation(aStart, aEnd, bEnd);
  const o3 = orientation(bStart, bEnd, aStart);
  const o4 = orientation(bStart, bEnd, aEnd);

  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && pointOnSegment(bStart, aStart, aEnd)) return true;
  if (o2 === 0 && pointOnSegment(bEnd, aStart, aEnd)) return true;
  if (o3 === 0 && pointOnSegment(aStart, bStart, bEnd)) return true;
  return o4 === 0 && pointOnSegment(aEnd, bStart, bEnd);
}

function orientation(
  a: THREE.Vector2,
  b: THREE.Vector2,
  c: THREE.Vector2,
): -1 | 0 | 1 {
  const value = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
  if (Math.abs(value) <= EPSILON) return 0;
  return value > 0 ? 1 : -1;
}

function pointOnSegment(
  point: THREE.Vector2,
  segmentStart: THREE.Vector2,
  segmentEnd: THREE.Vector2,
): boolean {
  const minX = Math.min(segmentStart.x, segmentEnd.x) - EPSILON;
  const maxX = Math.max(segmentStart.x, segmentEnd.x) + EPSILON;
  const minY = Math.min(segmentStart.y, segmentEnd.y) - EPSILON;
  const maxY = Math.max(segmentStart.y, segmentEnd.y) + EPSILON;
  if (point.x < minX || point.x > maxX || point.y < minY || point.y > maxY) {
    return false;
  }

  const segment = segmentEnd.clone().sub(segmentStart);
  const pointOffset = point.clone().sub(segmentStart);
  return (
    Math.abs(segment.x * pointOffset.y - segment.y * pointOffset.x) <= EPSILON
  );
}

function isPointInPolygon(
  point: THREE.Vector2,
  polygon: THREE.Vector2[],
): boolean {
  for (let index = 0; index < polygon.length; index += 1) {
    if (
      pointOnSegment(
        point,
        polygon[index],
        polygon[(index + 1) % polygon.length],
      )
    ) {
      return true;
    }
  }

  let inside = false;
  for (
    let index = 0, previousIndex = polygon.length - 1;
    index < polygon.length;
    previousIndex = index, index += 1
  ) {
    const current = polygon[index];
    const previous = polygon[previousIndex];
    const intersects =
      current.y > point.y !== previous.y > point.y &&
      point.x <
        ((previous.x - current.x) * (point.y - current.y)) /
          (previous.y - current.y || EPSILON) +
          current.x;

    if (intersects) inside = !inside;
  }

  return inside;
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}
